import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { HotKeys } from 'react-hotkeys';
import omit from 'lodash.omit';

import {
  getCardElementFrameStyle,
  getCardElementFrameContainerStyle,
} from './helpers';
import CardDesignerCardElementResizeControls from '../CardDesignerCardElementControls/components/CardDesignerCardElementResizeControls';
import CardDesignerCardElementDragControls from '../CardDesignerCardElementControls/components/CardDesignerCardElementDragControls';
import CardDesignerCardElementRotateControls from '../CardDesignerCardElementControls/components/CardDesignerCardElementRotateControls';
import useComponentBounds from '../../../../../../hooks/useComponentBounds';
import useDebounce from '../../../../../../hooks/useDebounce';
import { DESIGN_MODES } from '../../../constants';

const propTypes = {
  className: PropTypes.string,
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
    side: PropTypes.string.isRequired,
  }),
  cardBounds: PropTypes.shape({
    width: PropTypes.number,
  }),
  resizable: PropTypes.bool,
  draggable: PropTypes.bool,
  rotable: PropTypes.bool,
  isSelected: PropTypes.bool,
  onCardElementUpdate: PropTypes.func.isRequired,
  onCardElementDelete: PropTypes.func,
  onCardElementSelect: PropTypes.func,
  designMode: PropTypes.oneOf(Object.values(DESIGN_MODES)),
};

const CardDesignerCardElementFrame = ({
  className,
  element,
  cardBounds,
  children,
  resizable,
  draggable,
  rotable,
  isSelected,
  onCardElementUpdate,
  onCardElementDelete,
  onCardElementSelect,
  designMode,
}) => {
  const isHiddenIdCardElement =
    designMode === DESIGN_MODES.ID_CARDS &&
    element?.cardElementConfiguration?.opacity === 0;
  const [hasFinishedMoving, setHasFinishedMoving] = useState(false);
  const [debouncedHasFinishedMoving] = useDebounce(hasFinishedMoving, 50);
  const [updatingElement, setUpdatingElement] = useState(false);
  const [localElement, setLocalElement] = useState({
    ...element,
    cardElement: {
      ...element.cardElement,
      frame: { ...(element.cardElement.frame ?? {}) },
    },
  });
  const ref = useRef();
  const { bounds, recalculateBounds } = useComponentBounds(ref);
  const classes = classNames({
    [className]: !!className,
    'is-selected': isSelected,
    'card-designer-card-element-frame': true,
    'is-hidden-id-card-element': isHiddenIdCardElement,
    [`card-element-frame-${element.cardElement.type.toLowerCase()}`]: true,
  });

  // For the local element, we only care for the element's
  // cardElement & frame, so need to deep copy the rest
  useEffect(() => {
    setLocalElement({
      ...element,
      cardElement: {
        ...element.cardElement,
        frame: { ...(element.cardElement.frame ?? {}) },
      },
    });
    // We must force to recalculate bounds since things may have been dragged
    // which does *not* actually generate new bounds. Forcing recalculation
    // does allow us to properly resize from all 4 sides.
    recalculateBounds();
  }, [element]);

  useEffect(() => {
    const onUpdateElement = async () => {
      setUpdatingElement(true);
      setHasFinishedMoving(false);
      try {
        // No need to run if these are equal
        if (
          localElement.cardElement?.frame?.x ===
            element?.cardElement?.frame?.x &&
          localElement.cardElement?.frame?.y === element?.cardElement?.frame?.y
        ) {
          return;
        }
        setUpdatingElement(true);
        await onCardElementUpdate({
          cardElement: element.cardElement,
          data: {
            frame: {
              ...omit(localElement.cardElement.frame, '__typename'),
            },
          },
        });
      } catch {
        // Nothing to do.
      } finally {
        setUpdatingElement(false);
      }
    };
    if (debouncedHasFinishedMoving) {
      onUpdateElement();
    }
  }, [debouncedHasFinishedMoving]);

  useEffect(() => {
    if (isSelected && ref?.current) {
      ref?.current?.focus();
    }
  }, [isSelected, ref?.current]);

  const onMoveElementFrame = ({ x: deltaX, y: deltaY }) => {
    if (updatingElement) {
      return;
    }

    setHasFinishedMoving(false);

    const {
      x: xPercent,
      y: yPercent,
      width: widthPercent,
      height: heightPercent,
      rotation: frameRotation = 0,
    } = localElement?.cardElement?.frame;

    const { x, y } = bounds;

    // Account for frame rotation when computing the bounding box.
    const cardWidth = cardBounds?.width || 1;
    const cardHeight = cardBounds?.height || 1;
    const aspectH = cardHeight / cardWidth;
    const aspectW = cardWidth / cardHeight;
    const absCos = Math.abs(Math.cos(frameRotation));
    const absSin = Math.abs(Math.sin(frameRotation));
    const boundingWidthPercent =
      widthPercent * absCos + heightPercent * aspectH * absSin;
    const boundingHeightPercent =
      widthPercent * aspectW * absSin + heightPercent * absCos;

    const offsetX = (boundingWidthPercent - widthPercent) / 2;
    const offsetY = (boundingHeightPercent - heightPercent) / 2;

    let newX = Math.floor(x + deltaX);
    let newY = Math.floor(y + deltaY);

    // newX and newY should never be less or equal to than 0
    if (newX <= 0) {
      newX = Math.floor((x * 0.001) / Math.max(xPercent, 0.001));
    }
    if (newY <= 0) {
      newY = Math.floor((y * 0.001) / Math.max(yPercent, 0.001));
    }

    //  use Rule of 3 to determine the appropriate % value
    let newXPercent = parseFloat(
      ((newX * xPercent) / Math.max(x, 1)).toFixed(3),
    );
    let newYPercent = parseFloat(
      ((newY * yPercent) / Math.max(y, 1)).toFixed(3),
    );

    // Guard against NaN/Infinity from edge-case arithmetic
    if (!Number.isFinite(newXPercent)) newXPercent = xPercent;
    if (!Number.isFinite(newYPercent)) newYPercent = yPercent;

    // Clamp so the rotated bounding box stays within [0.001, 0.999].
    const minXPercent = 0.001 + offsetX;
    const maxXPercent = 0.999 - boundingWidthPercent + offsetX;
    const minYPercent = 0.001 + offsetY;
    const maxYPercent = 0.999 - boundingHeightPercent + offsetY;

    if (minXPercent > maxXPercent) {
      newXPercent = (minXPercent + maxXPercent) / 2;
    } else {
      if (newXPercent > maxXPercent) newXPercent = maxXPercent;
      if (newXPercent < minXPercent) newXPercent = minXPercent;
    }
    if (minYPercent > maxYPercent) {
      newYPercent = (minYPercent + maxYPercent) / 2;
    } else {
      if (newYPercent > maxYPercent) newYPercent = maxYPercent;
      if (newYPercent < minYPercent) newYPercent = minYPercent;
    }

    setLocalElement({
      ...localElement,
      cardElement: {
        ...localElement.cardElement,
        frame: {
          ...localElement.cardElement.frame,
          x: newXPercent,
          y: newYPercent,
        },
      },
    });
  };

  const keyMap = useMemo(
    () => ({
      DESELECT: ['esc', 'Escape'],
      DELETE: ['del', 'delete', 'backspace', 'Del', 'Delete', 'Backspace'],
      MOVE_LEFT: ['left'],
      MOVE_RIGHT: ['right'],
      MOVE_UP: ['up'],
      MOVE_DOWN: ['down'],
      MOVE_END: { sequence: ['left', 'right', 'up', 'down'], action: 'keyup' },
    }),
    [],
  );

  const keyHandlers = {
    DELETE: () => {
      onCardElementDelete?.(element.cardElement.id);
    },
    DESELECT: () => {
      onCardElementSelect?.();
    },
    MOVE_LEFT: () => {
      onMoveElementFrame({ x: -2, y: 0 });
    },
    MOVE_RIGHT: () => {
      onMoveElementFrame({ x: 2, y: 0 });
    },
    MOVE_DOWN: () => {
      onMoveElementFrame({ x: 0, y: 2 });
    },
    MOVE_UP: () => {
      onMoveElementFrame({ x: 0, y: -2 });
    },
    MOVE_END: () => {
      setHasFinishedMoving(true);
    },
  };

  return (
    <HotKeys keyMap={keyMap} handlers={keyHandlers}>
      <div
        tabIndex={0}
        ref={ref}
        className={classes}
        style={getCardElementFrameStyle(localElement)}
      >
        <div
          className="card-designer-card-element-frame-container"
          style={getCardElementFrameContainerStyle(localElement, bounds)}
        >
          {!isHiddenIdCardElement && children}
        </div>
        {isSelected && draggable && (
          <CardDesignerCardElementDragControls
            element={element}
            localElement={localElement}
            setLocalElement={setLocalElement}
            cardElementBounds={bounds}
            cardBounds={cardBounds}
            onCardElementUpdate={onCardElementUpdate}
          />
        )}
        {isSelected && resizable && (
          <CardDesignerCardElementResizeControls
            element={element}
            localElement={localElement}
            setLocalElement={setLocalElement}
            cardElementBounds={bounds}
            cardBounds={cardBounds}
            onCardElementUpdate={onCardElementUpdate}
          />
        )}
        {isSelected && rotable && (
          <CardDesignerCardElementRotateControls
            element={element}
            localElement={localElement}
            setLocalElement={setLocalElement}
            cardElementBounds={bounds}
            cardBounds={cardBounds}
            onCardElementUpdate={onCardElementUpdate}
          />
        )}
      </div>
    </HotKeys>
  );
};

CardDesignerCardElementFrame.propTypes = propTypes;

export default CardDesignerCardElementFrame;
